import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeftIcon,
    StarIcon,
    PlusIcon,
    ChartBarIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import { productsApi, reviewsApi } from '../utils/api';

// Import des composants d'avis
import ReviewsList from '../components/reviews/ReviewsList';
import ReviewForm from '../components/reviews/ReviewForm';
import ReviewStats from '../components/reviews/ReviewStats';

/**
 * Page dédiée à la gestion des avis d'un produit
 * Accessible depuis la liste des produits
 */
const ProductReviewsPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();

    // États pour le produit
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // États pour les avis
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewStats, setReviewStats] = useState(null);
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);

    // Charger les informations du produit
    const fetchProduct = async () => {
        try {
            setLoading(true);
            setError(null);

            // Utiliser l'API existante pour récupérer le produit
            const response = await productsApi.getById(productId);
            
            if (response && response.data) {
                setProduct(response.data);
            } else {
                throw new Error('Produit non trouvé');
            }

        } catch (err) {
            console.error('Erreur chargement produit:', err);
            setError(err.message || 'Erreur lors du chargement du produit');
        } finally {
            setLoading(false);
        }
    };

    // Effet pour charger le produit au montage
    useEffect(() => {
        if (productId) {
            fetchProduct();
        }
    }, [productId]);

    // Gérer la soumission d'un nouvel avis
    const handleReviewSubmit = async (reviewData, images) => {
        try {
            setIsSubmittingReview(true);
            
            // Créer l'avis via l'API
            const response = await reviewsApi.createReview(reviewData, images);
            
            if (response.data && response.data.success) {
                // Fermer le formulaire
                setShowReviewForm(false);
                // Les composants ReviewsList et ReviewStats se rechargeront automatiquement
            } else {
                throw new Error(response.data?.message || 'Erreur lors de la création de l\'avis');
            }
            
        } catch (error) {
            console.error('Erreur création avis:', error);
            throw error; // Le composant ReviewForm gèrera l'affichage de l'erreur
        } finally {
            setIsSubmittingReview(false);
        }
    };

    // Gérer la mise à jour des statistiques d'avis
    const handleStatsUpdate = (stats) => {
        setReviewStats(stats);
    };

    // Rendu du loading
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                        <span className="ml-2 text-gray-600">Chargement du produit...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Rendu de l'erreur
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center py-12">
                        <div className="text-red-600 mb-4">
                            <ChartBarIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-lg font-medium">Erreur de chargement</p>
                            <p className="text-sm">{error}</p>
                        </div>
                        <div className="space-x-3">
                            <button 
                                onClick={fetchProduct}
                                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                            >
                                Réessayer
                            </button>
                            <button 
                                onClick={() => navigate('/app/products')}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Retour à la liste
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
                
                {/* En-tête de page */}
                <div className="mb-8">
                    {/* Navigation */}
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                        <Link 
                            to="/app/products" 
                            className="hover:text-primary-600 transition-colors"
                        >
                            Produits
                        </Link>
                        <span>›</span>
                        <span className="text-gray-900">Avis du produit</span>
                    </div>

                    {/* Bouton retour */}
                    <button
                        onClick={() => navigate('/app/products')}
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-1" />
                        Retour à la liste des produits
                    </button>

                    {/* Informations du produit */}
                    {product && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                            <div className="flex items-center space-x-4">
                                {/* Image du produit */}
                                <div className="flex-shrink-0">
                                    {product.mainImage || product.main_image ? (
                                        <img
                                            src={product.mainImage || product.main_image}
                                            alt={product.name}
                                            className="h-16 w-16 rounded-lg object-cover border border-gray-200"
                                        />
                                    ) : (
                                        <div className="h-16 w-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Détails du produit */}
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-2xl font-bold text-gray-900 truncate">
                                        {product.name}
                                    </h1>
                                    <div className="flex items-center space-x-4 mt-1">
                                        <p className="text-sm text-gray-600">
                                            SKU: {product.sku || 'Non défini'}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            Catégorie: {product.category || product.product_type || 'Non définie'}
                                        </p>
                                    </div>
                                </div>

                                {/* Actions rapides */}
                                <div className="flex-shrink-0">
                                    <Link
                                        to={`/app/products/${productId}/edit`}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Éditer le produit
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Titre de la section avis */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Avis clients</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Gérez les avis et retours de vos clients sur ce produit
                            </p>
                        </div>

                        {/* Statistiques rapides dans l'en-tête */}
                        {reviewStats && reviewStats.totalReviews > 0 && (
                            <div className="flex items-center space-x-6 text-sm">
                                <div className="text-center">
                                    <div className="flex items-center justify-center space-x-1">
                                        <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
                                        <span className="font-semibold text-gray-900">
                                            {parseFloat(reviewStats.averageRating).toFixed(1)}
                                        </span>
                                    </div>
                                    <div className="text-gray-500">Note moyenne</div>
                                </div>
                                <div className="text-center">
                                    <div className="font-semibold text-gray-900">
                                        {reviewStats.totalReviews}
                                    </div>
                                    <div className="text-gray-500">
                                        Avis total{reviewStats.totalReviews > 1 ? 's' : ''}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions principales */}
                <div className="flex justify-between items-center mb-6">
                    <button
                        onClick={() => setShowReviewForm(!showReviewForm)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {showReviewForm ? 'Masquer le formulaire' : 'Ajouter un avis'}
                    </button>
                    
                    {reviewStats && (
                        <div className="text-sm text-gray-600">
                            {reviewStats.totalReviews === 0 
                                ? 'Aucun avis pour le moment' 
                                : `${reviewStats.totalReviews} avis sur ce produit`
                            }
                        </div>
                    )}
                </div>

                {/* Formulaire d'ajout d'avis */}
                {showReviewForm && (
                    <div className="mb-8">
                        <ReviewForm
                            productId={productId}
                            onSubmit={handleReviewSubmit}
                            onCancel={() => setShowReviewForm(false)}
                            isSubmitting={isSubmittingReview}
                        />
                    </div>
                )}

                {/* Contenu principal des avis */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Statistiques d'avis */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-6">
                            <ReviewStats 
                                productId={productId}
                                onStatsLoad={handleStatsUpdate}
                            />
                        </div>
                    </div>

                    {/* Liste des avis */}
                    <div className="lg:col-span-3">
                        <ReviewsList 
                            productId={productId}
                            onStatsUpdate={handleStatsUpdate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductReviewsPage;